import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AppDropdown from '../../components/AppDropdown';
import {
  Add,
  ArrowBack,
  DeleteOutlineOutlined,
  ReceiptLongOutlined,
  Save,
  PictureAsPdfOutlined,
  FileDownloadOutlined,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAllGoods } from '../../api/goodsApi';
import invoiceApi from '../../api/invoiceApi';
import { challanApi } from '../../api/challanApi';
import { searchCustomers } from '../../api/customerApi';
import backgroundPattern from '../../assets/pasbookpng.png';
import CustomerSearch from '../../components/CustomerSearch';
import './CreateInvoice.css';

const STATUS_OPTIONS = [
  { label: 'Draft', value: 'Draft' },
  { label: 'Sent', value: 'Sent' },
  { label: 'Paid', value: 'Paid' },
];

const localDate = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  return new Date(now.getTime() - offset * 60000).toISOString().slice(0, 10);
};

const money = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;


const sanitizeNumber = (value) => {
  const cleaned = String(value ?? '').replace(/[^0-9.]/g, '');
  const parts = cleaned.split('.');
  if (parts.length === 1) return parts[0];
  return `${parts[0]}.${parts.slice(1).join('')}`;
};

const getGoodsRecords = (response) => {
  const root = response?.data;
  const data = root?.result ?? root?.data ?? root;
  const records = data?.content ?? data?.items ?? data?.records ?? data ?? [];
  return Array.isArray(records) ? records : [];
};

const goodsId = (item) =>
  item?.id ?? item?.goodsItemId ?? item?.goodsId ?? item?.productId ?? null;

const goodsName = (item) =>
  item?.name || item?.productName || item?.goodsName || item?.description || item?.productCode || 'Unnamed item';

const goodsRate = (item) =>
  Number(item?.sellingPrice ?? item?.salePrice ?? item?.price ?? item?.unitPrice ?? 0) || 0;

const goodsUnit = (item) =>
  item?.unit || item?.unitName || item?.uom || '';

const goodsStock = (item) =>
  item?.stock ?? item?.availableStock ?? item?.quantity ?? null;

const newItem = () => ({
  id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  goodsItemId: null,
  description: '',
  quantity: 1,
  rate: 0,
  tax: 0,
  unit: '',
  stock: null,
});

const normalizeItem = (item) => ({
  id: item?.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  goodsItemId: item?.goodsItemId ?? item?.goodsId ?? item?.productId ?? item?.id ?? null,
  description: item?.description || item?.name || '',
  quantity: item?.quantity ?? item?.qty ?? 1,
  rate: item?.rate ?? item?.unitPrice ?? 0,
  tax: item?.tax ?? item?.taxRate ?? 0,
  unit: item?.unit || item?.unitName || '',
  stock: item?.stock ?? null,
});

const CreateInvoice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editingInvoice = location.state?.invoice;
  const quotationData = location.state?.quotationData || null;
  const salesOrderData = location.state?.salesOrderData || null;

  const getSourceRef = () => {
    if (quotationData) return { type: 'quotation', id: quotationData.id, number: quotationData.quotationNo || `EST-${String(quotationData.id || '').slice(-6)}` };
    if (salesOrderData) return { type: 'salesOrder', id: salesOrderData.id, number: salesOrderData.salesOrderNo || `SO-${String(salesOrderData.id || '').slice(-6)}` };
    if (editingInvoice?.sourceRef) return editingInvoice.sourceRef;
    return null;
  };

  const mapQuotationItems = () => {
    if (!quotationData?.cylinders?.length) return [newItem()];
    return quotationData.cylinders.map((row) => ({
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      goodsItemId: null,
      description: row.cylinderType || row.cylinderName || '',
      quantity: Number(row.qty || 1),
      rate: Number(row.rate || 0),
      tax: Number(row.gst || 0),
      unit: '',
      stock: null,
    }));
  };

  const mapSalesOrderItems = () => {
    if (!salesOrderData?.items?.length) return [newItem()];
    const gstPercent = parseInt(String(salesOrderData.tax || '').replace(/[^0-9]/g, '')) || 0;
    return salesOrderData.items.map((row) => ({
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      goodsItemId: null,
      description: row.item || '',
      quantity: Number(row.qty || 1),
      rate: Number(row.rate || 0),
      tax: gstPercent,
      unit: '',
      stock: null,
    }));
  };

  const [saving, setSaving] = useState(false);
  const [goods, setGoods] = useState([]);
  const [goodsLoading, setGoodsLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [challanNumber, setChallanNumber] = useState('');
  const [challanLoading, setChallanLoading] = useState(false);
  const [savedInvoiceId, setSavedInvoiceId] = useState(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  // Source reference from quotation or sales order
  const sourceRef = getSourceRef();
  const [invoice, setInvoice] = useState({
    invoiceNumber: `INV-${Date.now().toString().slice(-6)}`,
    invoiceDate: localDate(),
    dueDate: '',
    status: 'Draft',
    customerId: salesOrderData?.customerId || quotationData?.customerId || null,
    customerName: salesOrderData?.customer || quotationData?.customer?.name || '',
    customerPhone: salesOrderData?.customerPhone || quotationData?.customer?.mobile || '',
    customerEmail: salesOrderData?.customerEmail || quotationData?.customer?.email || '',
    customerAddress: salesOrderData?.customerAddress || quotationData?.customer?.address || '',
    notes: salesOrderData?.notes || quotationData?.notes || '',
    terms: salesOrderData?.terms || quotationData?.terms || '',
    discount: 0,
    items: editingInvoice?.items?.length ? editingInvoice.items : salesOrderData ? mapSalesOrderItems() : mapQuotationItems(),
  });

  useEffect(() => {
    let active = true;

    const loadGoods = async () => {
      setGoodsLoading(true);
      try {
        const response = await getAllGoods();
        if (active) setGoods(getGoodsRecords(response));
      } catch (error) {
        console.error('Failed to load goods for invoice:', error);
        if (active) {
          setGoods([]);
          toast.error('Unable to load goods. You can retry the page.');
        }
      } finally {
        if (active) setGoodsLoading(false);
      }
    };

    loadGoods();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!editingInvoice) return;

    setInvoice({
      invoiceNumber: editingInvoice.invoiceNumber || '',
      invoiceDate: editingInvoice.invoiceDate || localDate(),
      dueDate: editingInvoice.dueDate || '',
      status: editingInvoice.status || 'Draft',
      customerId: editingInvoice.customerId ?? null,
      customerName: editingInvoice.customerName || '',
      customerPhone: editingInvoice.customerPhone || '',
      customerEmail: editingInvoice.customerEmail || '',
      customerAddress: editingInvoice.customerAddress || '',
      notes: editingInvoice.notes || '',
      terms: editingInvoice.terms || '',
      discount: editingInvoice.discount ?? editingInvoice.discountPercent ?? 0,
      items: editingInvoice.items?.length
        ? editingInvoice.items.map(normalizeItem)
        : [newItem()],
    });
  }, [editingInvoice]);

  const update = (field, value) => {
    setInvoice((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const updateItem = (id, field, value) => {
    setInvoice((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      ),
    }));
    setErrors((prev) => ({ ...prev, items: undefined }));
  };

  const addItem = () => {
    setInvoice((prev) => ({ ...prev, items: [...prev.items, newItem()] }));
  };

  const removeItem = (id) => {
    setInvoice((prev) => ({
      ...prev,
      items:
        prev.items.length > 1
          ? prev.items.filter((item) => item.id !== id)
          : prev.items,
    }));
  };

  const totals = useMemo(() => {
    const subtotal = invoice.items.reduce(
      (sum, item) => sum + Number(item.quantity || 0) * Number(item.rate || 0),
      0
    );

    const tax = invoice.items.reduce((sum, item) => {
      const base = Number(item.quantity || 0) * Number(item.rate || 0);
      return sum + (base * Number(item.tax || 0)) / 100;
    }, 0);

    const discount = Math.min(Math.max(Number(invoice.discount || 0), 0), 100);
    const discountAmount = (subtotal * discount) / 100;

    return {
      subtotal,
      tax,
      discount,
      discountAmount,
      total: Math.max(subtotal + tax - discountAmount, 0),
    };
  }, [invoice.items, invoice.discount]);

  const validate = () => {
    const next = {};

    if (!invoice.customerName.trim()) {
      next.customerName = 'Customer is required.';
    }

    const validItems = invoice.items.filter((item) => item.description.trim());
    if (!validItems.length) {
      next.items = 'Add at least one product or service.';
    }

    if (invoice.dueDate && invoice.invoiceDate && invoice.dueDate < invoice.invoiceDate) {
      next.dueDate = 'Due date cannot be before invoice date.';
    }

    validItems.forEach((item) => {
      if (Number(item.quantity) <= 0) next[`qty-${item.id}`] = 'Qty must be greater than 0.';
      if (Number(item.rate) < 0) next[`rate-${item.id}`] = 'Rate cannot be negative.';
    });

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildPayload = (status = invoice.status) => ({
    invoiceNumber: invoice.invoiceNumber.trim(),
    invoiceDate: invoice.invoiceDate,
    dueDate: invoice.dueDate || null,
    status,
    customerId: invoice.customerId,
    customerName: invoice.customerName.trim(),
    customerPhone: invoice.customerPhone.trim(),
    customerEmail: invoice.customerEmail.trim(),
    customerAddress: invoice.customerAddress.trim(),
    notes: invoice.notes.trim(),
    terms: invoice.terms.trim(),
    discount: totals.discount,
    discountAmount: totals.discountAmount,
    subtotal: totals.subtotal,
    taxAmount: totals.tax,
    total: totals.total,
    sourceRef: sourceRef,
    items: invoice.items
      .filter((item) => item.description.trim())
      .map((item) => ({
        goodsItemId: item.goodsItemId ? Number(item.goodsItemId) || item.goodsItemId : null,
        description: item.description.trim(),
        quantity: Number(item.quantity) || 0,
        rate: Number(item.rate) || 0,
        tax: Number(item.tax) || 0,
        unit: item.unit || '',
        amount:
          Number(item.quantity || 0) * Number(item.rate || 0) *
          (1 + Number(item.tax || 0) / 100),
      })),
  });

  const save = async (status, draftOnly = false) => {
    if (draftOnly) {
      if (!invoice.customerName.trim() && !invoice.items.some((item) => item.description.trim())) {
        toast.error('Add a customer or product before saving the draft.');
        return;
      }
    } else if (!validate()) {
      toast.error('Please fix the highlighted fields.');
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload(status);
      let savedInvoice;
      if (editingInvoice?.id) {
        const response = await invoiceApi.updateInvoice(editingInvoice.id, payload);
        savedInvoice = response.data;
        toast.success(draftOnly ? 'Draft updated.' : 'Invoice updated successfully.');
      } else {
        const response = await invoiceApi.createInvoice(payload);
        savedInvoice = response.data;
        toast.success(draftOnly ? 'Draft saved.' : 'Invoice created successfully.');
      }
      if (savedInvoice?.id) {
        setSavedInvoiceId(savedInvoice.id);
      }
      // navigate('/dashboard/invoices/view', { replace: true });
    } catch (error) {
      console.error('Invoice save failed:', error);
      toast.error(error?.response?.data?.message || 'Unable to save invoice. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async (invoiceId) => {
    setPdfDownloading(true);
    try {
      const response = await invoiceApi.downloadInvoicePdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Tax_Invoice_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF download failed:', error);
      toast.error('Failed to download PDF');
    } finally {
      setPdfDownloading(false);
    }
  };

  const handlePreviewPdf = async (invoiceId) => {
    try {
      const response = await invoiceApi.previewInvoicePdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('PDF preview failed:', error);
      toast.error('Failed to preview PDF');
    }
  };

  const handleCustomerSelect = (customer) => {
    if (!customer) {
      update('customerId', null);
      update('customerName', '');
      update('customerPhone', '');
      update('customerEmail', '');
      update('customerAddress', '');
      return;
    }

    update('customerId', customer.id || null);
    update('customerName', customer.name || customer.customerName || '');
    update('customerPhone', customer.contactNumber || customer.mobile || customer.phone || '');
    update('customerEmail', customer.email || customer.customerEmail || '');
    update('customerAddress', customer.address || customer.customerAddress || '');
  };

  const handleChallanSearch = async () => {
    if (!challanNumber.trim()) {
      toast.error('Please enter a challan number.');
      return;
    }

    setChallanLoading(true);
    try {
      const response = await challanApi.searchChallans({ challanNumber, page: 0, size: 10 });
      const challans = response.data?.content || [];
      
      if (challans.length === 0) {
        toast.error('No challan found with this number.');
        return;
      }

      const challan = challans[0];
      
      // Fetch full customer details by ID
      let customerDetails = {};
      if (challan.customerId) {
        try {
          const custRes = await searchCustomers({ name: challan.customerName || '' });
          const custList = Array.isArray(custRes?.data?.data) ? custRes.data.data : Array.isArray(custRes?.data) ? custRes.data : [];
          customerDetails = custList.find(c => String(c.id) === String(challan.customerId)) || {};
        } catch (e) { /* ignore */ }
      }

      // Populate customer details
      update('customerId', challan.customerId);
      update('customerName', challan.customerName || '');
      update('customerPhone', customerDetails.contactNumber || customerDetails.mobile || customerDetails.phone || '');
      update('customerEmail', customerDetails.email || customerDetails.customerEmail || '');
      
      // Trigger customer selection to update CustomerSearch component
      handleCustomerSelect({
        id: challan.customerId,
        name: challan.customerName,
        contactNumber: customerDetails.contactNumber || customerDetails.mobile || '',
        email: customerDetails.email || '',
      });
      
      // Populate items from challan
      if (challan.items && challan.items.length > 0) {
        // Try to get rates from source Sales Order via purchaseOrderNo (SO number)
        let soItems = [];
        let soTaxPct = 0;
        const soNumber = challan.purchaseOrderNo;
        if (soNumber) {
          try {
            const soList = JSON.parse(localStorage.getItem('salesOrders') || '[]');
            const so = soList.find(s => String(s.salesOrderNo || s.noteNo || '') === String(soNumber));
            if (so?.items) soItems = so.items;
            if (so?.tax) soTaxPct = parseInt(String(so.tax).replace(/[^0-9]/g, '')) || 0;
          } catch (e) { /* ignore */ }
        }

        const invoiceItems = challan.items.map((item) => {
          // 1st: Find matching SO item by name to get rate
          const soItem = soItems.find(si => (si.item || '').toLowerCase() === (item.name || '').toLowerCase());
          let rate = Number(soItem?.rate || 0);

          // 2nd: Fallback to goods inventory rate
          if (!rate && item.goodsItemId) {
            const good = goods.find(g => String(goodsId(g)) === String(item.goodsItemId));
            if (good) rate = goodsRate(good);
          }

          // 3rd: Fallback to goods inventory by name match
          if (!rate && item.name) {
            const good = goods.find(g => goodsName(g).toLowerCase() === item.name.toLowerCase());
            if (good) rate = goodsRate(good);
          }

          // Tax from SO or goods
          let tax = soTaxPct;
          if (!tax && item.goodsItemId) {
            const good = goods.find(g => String(goodsId(g)) === String(item.goodsItemId));
            if (good) tax = Number(good.taxRate || good.tax || good.gst || 0);
          }

          return {
            id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            goodsItemId: item.goodsItemId,
            description: item.name || '',
            quantity: item.deliveredQty || item.receivedQty || 1,
            rate,
            tax,
            unit: '',
            stock: null,
          };
        });
        
        setInvoice((prev) => ({ ...prev, items: invoiceItems }));
      }

      toast.success('Challan details loaded successfully.');
    } catch (error) {
      console.error('Failed to fetch challan:', error);
      toast.error('Unable to fetch challan. Please try again.');
    } finally {
      setChallanLoading(false);
    }
  };

  const selectGoods = (itemId, selected) => {
    if (!selected) {
      updateItem(itemId, 'goodsItemId', null);
      return;
    }

    updateItem(itemId, 'goodsItemId', goodsId(selected));
    updateItem(itemId, 'description', goodsName(selected));
    updateItem(itemId, 'rate', goodsRate(selected));
    updateItem(itemId, 'unit', goodsUnit(selected));
    updateItem(itemId, 'stock', goodsStock(selected));
  };

  const productValue = (item) => {
    if (!item.goodsItemId) return null;
    return goods.find((good) => String(goodsId(good)) === String(item.goodsItemId)) || {
      id: item.goodsItemId,
      name: item.description,
    };
  };

  return (
    <Box className="ci-page ci-create-page">
      <div
        className="ci-background-watermark"
        style={{ backgroundImage: `url(${backgroundPattern})` }}
      />

      <Box className="ci-shell">
        <Box className="ci-toolbar">
          <Box className="ci-toolbar-left ci-header-title-group">
            <Box className="ci-page-icon"><ReceiptLongOutlined /></Box>
            <Box className="ci-page-title-wrap">
              <Typography className="ci-page-title">
                {editingInvoice ? 'Edit Invoice' : 'Create Invoice'}
              </Typography>
              <Typography className="ci-page-subtitle">
                {quotationData
                  ? `Created from ${quotationData.quotationNo || 'Quotation'}`
                  : salesOrderData
                    ? `Created from ${salesOrderData.salesOrderNo || 'Sales Order'}`
                    : editingInvoice
                      ? 'Update invoice details and save your changes.'
                      : 'Create a professional invoice for your customer.'}
              </Typography>
            </Box>
          </Box>
          <IconButton
            className="ci-header-back"
            onClick={() => navigate('/dashboard/invoices/view')}
            aria-label="Back to invoices"
          >
            <ArrowBack />
          </IconButton>
        </Box>

        <Card className="ci-create-card">
          <Box className="ci-card-section ci-main-details">
            <Box className="ci-section-heading">
              <Box>
                <Typography>Invoice details</Typography>
                <span>Basic information</span>
              </Box>
            </Box>
            <Grid className="ci-details-grid" container spacing={2.5} rowSpacing={2}>
              <Grid item xs={12} sm={6}>
                <Box className="customer-field">
                  <label>Invoice Number</label>
                  <TextField fullWidth value={invoice.invoiceNumber}
                    onChange={(e) => update('invoiceNumber', e.target.value)} className="ci-field" />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box className="customer-field">
                  <label>Invoice Date</label>
                  <TextField fullWidth type="date" InputLabelProps={{ shrink: true }}
                    value={invoice.invoiceDate} onChange={(e) => update('invoiceDate', e.target.value)} className="ci-field ci-date-field" />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box className="customer-field">
                  <label>Due Date</label>
                  <TextField fullWidth type="date" InputLabelProps={{ shrink: true }}
                    value={invoice.dueDate} onChange={(e) => update('dueDate', e.target.value)}
                    className="ci-field ci-date-field" error={Boolean(errors.dueDate)} helperText={errors.dueDate} />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box className="customer-field">
                  <label>Status</label>
                  <AppDropdown
                    value={invoice.status}
                    options={STATUS_OPTIONS}
                    onChange={(value) => update('status', value)}
                    placeholder="Select status"
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box className="ci-card-section">
            <Box className="ci-section-heading">
              <Box>
                <Typography>Bill to</Typography>
                <span>Customer information</span>
              </Box>
            </Box>
            <Grid className="ci-customer-grid" container spacing={2.5} rowSpacing={2}>
              <Grid item xs={12} sm={6}>
                <CustomerSearch
                  onSelectCustomer={handleCustomerSelect}
                  selectedCustomer={invoice.customerName ? { name: invoice.customerName } : null}
                />
                {errors.customerName && <Typography className="ci-inline-error">{errors.customerName}</Typography>}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box className="customer-field">
                  <label>Phone Number</label>
                  <TextField fullWidth placeholder="Customer phone" value={invoice.customerPhone}
                    onChange={(e) => update('customerPhone', e.target.value)} className="ci-field" />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box className="customer-field">
                  <label>Email</label>
                  <TextField fullWidth placeholder="Customer email" value={invoice.customerEmail}
                    onChange={(e) => update('customerEmail', e.target.value)} className="ci-field" />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box className="customer-field">
                  <label>Challan Number (Optional)</label>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      fullWidth
                      placeholder="Enter challan number"
                      value={challanNumber}
                      onChange={(e) => setChallanNumber(e.target.value)}
                      className="ci-field"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleChallanSearch();
                        }
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleChallanSearch}
                      disabled={challanLoading}
                      className="ci-outline-btn"
                    >
                      {challanLoading ? 'Loading...' : 'Load'}
                    </Button>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box className="ci-card-section ci-items-section">
            <Box className="ci-section-heading-row">
              <Box className="ci-section-heading no-bottom">
                <Box>
                  <Typography>Items & services</Typography>
                  <span>Select goods from inventory or enter a custom service.</span>
                </Box>
              </Box>
              <Box className="ci-items-add-desktop">
                <Button className="ci-outline-btn" startIcon={<Add />} onClick={addItem} disabled={saving}>
                  Add item
                </Button>
              </Box>
            </Box>

            {goodsLoading && <Box className="ci-goods-loading">Loading goods from inventory…</Box>}
            {errors.items && <Typography className="ci-validation">{errors.items}</Typography>}

            <Box className="ci-items-table-wrap">
              <table className="ci-items-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product / Description</th>
                    <th>Qty</th>
                    <th>Rate</th>
                    <th>Tax</th>
                    <th>Amount</th>
                    <th aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item, index) => {
                    const base = Number(item.quantity || 0) * Number(item.rate || 0);
                    const amount = base + (base * Number(item.tax || 0)) / 100;
                    const selectedProduct = productValue(item);

                    return (
                      <tr key={item.id} className="ci-item-row">
                        <td>
                          <Box className="ci-row-number">{index + 1}</Box>
                        </td>
                        <td className="ci-product-cell" data-label="Product / description">
                          <Autocomplete
                            size="small"
                            fullWidth
                            options={goods}
                            loading={goodsLoading}
                            value={selectedProduct}
                            inputValue={item.description || ''}
                            freeSolo
                            onChange={(_, value) => {
                              if (typeof value === 'string') {
                                updateItem(item.id, 'goodsItemId', null);
                                updateItem(item.id, 'description', value);
                                return;
                              }
                              selectGoods(item.id, value);
                            }}
                            onInputChange={(_, value, reason) => {
                              if (reason === 'input') {
                                updateItem(item.id, 'goodsItemId', null);
                                updateItem(item.id, 'description', value);
                              }
                            }}
                            getOptionLabel={(option) => typeof option === 'string' ? option : goodsName(option)}
                            isOptionEqualToValue={(option, value) => String(goodsId(option)) === String(goodsId(value))}
                            noOptionsText={goods.length ? 'No matching goods' : 'No goods available'}
                            renderOption={(props, option) => (
                              <Box component="li" {...props} key={String(goodsId(option))} className="ci-product-option">
                                <Box>
                                  <strong>{goodsName(option)}</strong>
                                  <span>
                                  {option.productCode || option.sku || 'Inventory item'}
                                  {goodsUnit(option) ? ` · ${goodsUnit(option)}` : ''}
                                  {goodsStock(option) !== null && goodsStock(option) !== undefined ? ` · Stock ${goodsStock(option)}` : ''}
                                </span>
                                </Box>
                                <strong>{money(goodsRate(option))}</strong>
                              </Box>
                            )}
                            renderInput={(params) => (
                              <TextField {...params} placeholder="Search goods or enter service" className="ci-field" />
                            )}
                          />
                          {item.goodsItemId && item.stock !== null && item.stock !== undefined && (
                            <span className="ci-stock-hint">Available stock: {item.stock}{item.unit ? ` ${item.unit}` : ''}</span>
                          )}
                        </td>
                        <td data-label="Qty">
                          <TextField fullWidth size="small" type="text" inputMode="decimal" value={item.quantity}
                            onChange={(e) => updateItem(item.id, 'quantity', sanitizeNumber(e.target.value))}
                            onKeyDown={(e) => { if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); }}
                            inputProps={{ inputMode: 'decimal', pattern: '[0-9.]*' }} className="ci-field ci-number-field"
                            error={Boolean(errors[`qty-${item.id}`])} />
                        </td>
                        <td data-label="Rate">
                          <TextField fullWidth size="small" type="text" inputMode="decimal" value={item.rate}
                            onChange={(e) => updateItem(item.id, 'rate', sanitizeNumber(e.target.value))}
                            onKeyDown={(e) => { if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); }}
                            inputProps={{ inputMode: 'decimal', pattern: '[0-9.]*' }} className="ci-field ci-number-field"
                            error={Boolean(errors[`rate-${item.id}`])}
                            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                        </td>
                        <td data-label="Tax">
                          <TextField fullWidth size="small" type="text" inputMode="decimal" value={item.tax}
                            onChange={(e) => updateItem(item.id, 'tax', sanitizeNumber(e.target.value))}
                            onKeyDown={(e) => { if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); }}
                            inputProps={{ inputMode: 'decimal', pattern: '[0-9.]*' }} className="ci-field ci-number-field"
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                        </td>
                        <td className="amount-cell" data-label="Amount">
                          <Typography className="ci-line-total">{money(amount)}</Typography>
                        </td>
                        <td>
                          <IconButton className="ci-remove-btn" disabled={invoice.items.length === 1 || saving}
                            onClick={() => removeItem(item.id)} aria-label="Remove item">
                            <DeleteOutlineOutlined />
                          </IconButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>

            <Box className="ci-items-add-mobile">
              <Button
                className="ci-outline-btn"
                startIcon={<Add />}
                onClick={addItem}
                disabled={saving}
                fullWidth
              >
                Add product
              </Button>
            </Box>

            <Typography className="ci-items-note">
              Amount is calculated from quantity, rate and item tax. Goods pricing is prefilled from inventory and can be adjusted for this invoice.
            </Typography>
          </Box>

          <Divider />

          <Box className="ci-card-section ci-bottom-section">
            <Box className="ci-bottom-grid">
              <Box className="ci-subsection">
                <Typography className="ci-subsection-title">Notes</Typography>
                <TextField fullWidth multiline minRows={4} maxRows={7} placeholder="Add a note for your customer…"
                  value={invoice.notes} onChange={(e) => update('notes', e.target.value)} className="ci-field" />
              </Box>
              <Box className="ci-subsection">
                <Typography className="ci-subsection-title">Terms & conditions</Typography>
                <TextField fullWidth multiline minRows={4} maxRows={7} placeholder="Add payment terms, return policy or other conditions…"
                  value={invoice.terms} onChange={(e) => update('terms', e.target.value)} className="ci-field" />
              </Box>
              <Box className="ci-total-box ci-bottom-total-box">
                <Stack className="ci-total-row"><span>Subtotal</span><strong>{money(totals.subtotal)}</strong></Stack>
                <Stack className="ci-total-row ci-discount-row">
                  <span>Discount</span>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField size="small" type="text" inputMode="decimal" value={invoice.discount}
                      onChange={(e) => update('discount', sanitizeNumber(e.target.value))} 
                      onKeyDown={(e) => { if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); }}
                      className="ci-discount-input"
                      inputProps={{ inputMode: 'decimal', pattern: '[0-9.]*' }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                    <strong>-{money(totals.discountAmount)}</strong>
                  </Stack>
                </Stack>
                <Stack className="ci-total-row"><span>Tax</span><strong>{money(totals.tax)}</strong></Stack>
                <Divider />
                <Stack className="ci-grand-total">
                  <Box><Typography>Total</Typography><span>Amount payable</span></Box>
                  <strong>{money(totals.total)}</strong>
                </Stack>
              </Box>
            </Box>
          </Box>
        </Card>

        <Stack className="ci-save-bar" direction="row" justifyContent="flex-end" spacing={1.25}>
          <Button className="ci-cancel-btn" onClick={() => navigate('/dashboard/invoices/view')} disabled={saving}>Cancel</Button>
          <Button className="ci-secondary-btn" onClick={() => save('Draft', true)} disabled={saving}>Save as draft</Button>
          <Button className="ci-primary-btn" startIcon={<Save />} onClick={() => save(invoice.status, false)} disabled={saving}>
            {saving ? 'Saving…' : editingInvoice ? 'Update invoice' : 'Save invoice'}
          </Button>
          {savedInvoiceId && !saving && (
            <>
              <Button className="ci-secondary-btn" startIcon={<PictureAsPdfOutlined />} onClick={() => handlePreviewPdf(savedInvoiceId)} disabled={pdfDownloading}>
                {pdfDownloading ? 'Loading…' : 'Preview PDF'}
              </Button>
              <Button className="ci-secondary-btn" startIcon={<FileDownloadOutlined />} onClick={() => handleDownloadPdf(savedInvoiceId)} disabled={pdfDownloading}>
                {pdfDownloading ? 'Downloading…' : 'Download PDF'}
              </Button>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default CreateInvoice;